import { useState } from 'react';
import { useRole } from '@/contexts/RoleContext';
import { useAdminDashboardData, computeOrgStats, computeMentorsForOrg } from '@/hooks/useAdminDashboardData';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Users, AlertTriangle, Trophy, TrendingUp, Building2 } from 'lucide-react';
import { useOrgFilterStore } from '@/hooks/useOrgFilterStore';

export function AdminDashboard() {
  const { currentRole } = useRole();
  const isSuperAdmin = currentRole === 'super_admin';
  const [selectedOrg, setSelectedOrg] = useOrgFilterStore();

  const { data, isLoading } = useAdminDashboardData();

  if (isLoading || !data) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  const organizations = data.organizations;
  const currentOrgId = selectedOrg === 'all' ? null : selectedOrg;

  const stats = computeOrgStats(data, isSuperAdmin ? currentOrgId : (data.profiles[0]?.organization_id ?? null));
  const mentors = computeMentorsForOrg(data, isSuperAdmin ? currentOrgId : (data.profiles[0]?.organization_id ?? null));

  // Super admin "all" overview
  const orgOverview = isSuperAdmin && selectedOrg === 'all'
    ? organizations.map(org => {
        const orgStats = computeOrgStats(data, org.id);
        const orgMentors = computeMentorsForOrg(data, org.id);
        return {
          ...org,
          participantsCount: orgStats.totalParticipants,
          mentorsCount: orgMentors.length,
          avgProgress: orgStats.avgProgress,
          demodayReady: orgStats.demodayReadyCount,
        };
      })
    : null;

  const totalStats = orgOverview
    ? {
        totalOrgs: orgOverview.length,
        totalParticipants: orgOverview.reduce((a, o) => a + o.participantsCount, 0),
        totalMentors: new Set(data.mentorRecords.map(m => m.user_id)).size,
        avgProgress: orgOverview.length > 0
          ? Math.round(orgOverview.reduce((a, o) => a + o.avgProgress, 0) / orgOverview.length)
          : 0,
      }
    : null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-display font-bold text-foreground">
          {isSuperAdmin ? 'Panel Super Administrador' : 'Panel de Administración'}
        </h1>

        {isSuperAdmin && (
          <Select value={selectedOrg} onValueChange={setSelectedOrg}>
            <SelectTrigger className="w-[280px]">
              <Building2 className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Seleccionar organización" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                <span className="flex items-center gap-2">🌐 Todas las organizaciones</span>
              </SelectItem>
              {organizations.map(org => (
                <SelectItem key={org.id} value={org.id}>
                  <span className="flex items-center gap-2">
                    {org.logo_url ? (
                      <img src={org.logo_url} className="w-4 h-4 rounded" alt="" />
                    ) : (
                      '🏢'
                    )}{' '}
                    {org.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Super Admin: Org overview cards */}
      {isSuperAdmin && selectedOrg === 'all' && totalStats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="glass-card border-primary/30">
            <CardContent className="p-6">
              <Building2 className="w-8 h-8 text-primary mb-2" />
              <p className="text-3xl font-bold">{totalStats.totalOrgs}</p>
              <p className="text-sm text-muted-foreground">Organizaciones</p>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="p-6">
              <Users className="w-8 h-8 text-secondary mb-2" />
              <p className="text-3xl font-bold">{totalStats.totalParticipants}</p>
              <p className="text-sm text-muted-foreground">Total Participantes</p>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="p-6">
              <Trophy className="w-8 h-8 text-accent mb-2" />
              <p className="text-3xl font-bold">{totalStats.totalMentors}</p>
              <p className="text-sm text-muted-foreground">Total Mentores</p>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="p-6">
              <TrendingUp className="w-8 h-8 text-info mb-2" />
              <p className="text-3xl font-bold">{totalStats.avgProgress}%</p>
              <p className="text-sm text-muted-foreground">Progreso Promedio Global</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Super Admin: Organizations table */}
      {isSuperAdmin && selectedOrg === 'all' && orgOverview && (
        <Card className="glass-card">
          <CardHeader>
            <CardTitle>Organizaciones</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {orgOverview.length === 0 && (
                <p className="text-muted-foreground text-center py-8">No hay organizaciones registradas</p>
              )}
              {orgOverview.map(org => (
                <div
                  key={org.id}
                  className="flex items-center justify-between p-4 bg-muted/50 rounded-xl hover:bg-muted/70 transition-colors cursor-pointer"
                  onClick={() => setSelectedOrg(org.id)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-2xl">
                      {org.logo_url ? (
                        <img src={org.logo_url} className="w-8 h-8 rounded object-cover" alt="" />
                      ) : (
                        '🏢'
                      )}
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">{org.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {org.participantsCount} participantes · {org.mentorsCount} mentores
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-lg font-bold text-foreground">{org.avgProgress}%</p>
                      <p className="text-xs text-muted-foreground">Progreso</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-success">{org.demodayReady}</p>
                      <p className="text-xs text-muted-foreground">Demoday Ready</p>
                    </div>
                    <Progress value={org.avgProgress} className="w-24 h-2" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Regular stats cards (shown when org is selected or for regular admin) */}
      {(!isSuperAdmin || selectedOrg !== 'all') && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="glass-card">
              <CardContent className="p-6">
                <Users className="w-8 h-8 text-primary mb-2" />
                <p className="text-3xl font-bold">{stats.totalParticipants}</p>
                <p className="text-sm text-muted-foreground">Participantes</p>
              </CardContent>
            </Card>
            <Card className="glass-card border-success/30">
              <CardContent className="p-6">
                <Trophy className="w-8 h-8 text-success mb-2" />
                <p className="text-3xl font-bold text-success">{stats.demodayReadyCount}</p>
                <p className="text-sm text-muted-foreground">Listos Demoday</p>
              </CardContent>
            </Card>
            <Card className="glass-card border-warning/30">
              <CardContent className="p-6">
                <AlertTriangle className="w-8 h-8 text-warning mb-2" />
                <p className="text-3xl font-bold text-warning">{stats.atRiskCount}</p>
                <p className="text-sm text-muted-foreground">Sin Proyecto</p>
              </CardContent>
            </Card>
            <Card className="glass-card">
              <CardContent className="p-6">
                <TrendingUp className="w-8 h-8 text-info mb-2" />
                <p className="text-3xl font-bold">{stats.avgProgress}%</p>
                <p className="text-sm text-muted-foreground">Progreso Promedio</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="glass-card">
              <CardHeader>
                <CardTitle>Proyectos de la Ruta</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span>Total</span>
                    <span className="font-bold">{stats.totalDeliverables}</span>
                  </div>
                  <div className="flex justify-between text-success">
                    <span>Evaluados</span>
                    <span className="font-bold">{stats.validatedDeliverables}</span>
                  </div>
                  <Progress
                    value={
                      stats.totalDeliverables > 0
                        ? (stats.validatedDeliverables / stats.totalDeliverables) * 100
                        : 0
                    }
                    className="h-3"
                  />
                </div>
              </CardContent>
            </Card>
            <Card className="glass-card">
              <CardHeader>
                <CardTitle>Mentores ({mentors.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {mentors.length === 0 && (
                    <p className="text-muted-foreground text-center py-4">No hay mentores asignados</p>
                  )}
                  {mentors.map(m => (
                    <div
                      key={m.id}
                      className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <Avatar className="w-8 h-8">
                          <AvatarImage src={m.avatar} />
                          <AvatarFallback className="bg-primary/10 text-primary text-xs">
                            {m.name
                              .split(' ')
                              .map(n => n[0])
                              .join('')
                              .slice(0, 2)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-medium">{m.name}</span>
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {m.participantCount} participantes
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
